import { useTranslation } from "react-i18next";
import { InspectionContext } from "../types";
import TextInput from "@/components/atoms/TextInput";
import Checkbox from "@/components/atoms/Checkbox";
import Text from "@/components/atoms/Text";
import PhotoInput from "@/components/molecules/PhotoInput";
import WeightInput from "@/components/molecules/WeightInput";
import { SummaryStrip } from "../components/FormParts";
import { UnitNumberInput } from "../components/FormParts";
import FractionsTable from "../components/FractionsTable";
import { FieldLabel } from "../components/FormParts";
import { InspectionFormApi, InspectionSummary } from "../useInspectionForm";
import { useInspectionFormat } from "../useInspectionFormat";
import {
  InspectionErrors,
  MAX_PHOTOS_PER_STREAM,
  MAX_PO_NUMBER_LENGTH,
  MAX_REGISTRATION_NUMBER_LENGTH,
} from "../validation";
import styles from "../styles.module.css";

const noop = () => {};

interface WeighingStepProps {
  form: InspectionFormApi;
  summary: InspectionSummary;
  context: InspectionContext;
  /** Errors to show: empty until the inspector has tried to continue. */
  errors: InspectionErrors;
}

/** Step 1, "Weight and receipt": the approved weight, photos, extra fractions and whether there's a deviation. */
export default function WeighingStep({ form, summary, context, errors }: WeighingStepProps) {
  const { t } = useTranslation();
  const format = useInspectionFormat();
  const { values } = form;
  const original = values.streams[0];
  const contract = context.order.contract;
  const error = (path: string) => (errors[path] ? t(errors[path]) : undefined);

  return (
    <div className={styles.stepBody}>
      <SummaryStrip
        items={[
          {
            label: t("inspections:summary.product"),
            value: (
              <>
                {context.order.productName}{" "}
                {contract?.price != null && contract.priceUnit && (
                  <Text span color="secondary">
                    ({format.unitPrice(contract.price, contract.currency, contract.priceUnit.unit)})
                  </Text>
                )}
              </>
            ),
          },
          { label: t("inspections:summary.contract"), value: contract?.contractName ?? "—" },
          {
            label: t("inspections:summary.orderedAmount"),
            value: `${format.number(context.order.amount)} ${context.order.unit.unit}`,
          },
        ]}
      />

      <div className={styles.twoColumns}>
        <div className={styles.fieldStack}>
          {values.multiFraction ? (
            // With several fractions the total is their sum, in kg, so it's read-only here.
            <UnitNumberInput
              label={t("inspections:weighing.approvedWeight")}
              withAsterisk
              unit="kg"
              allowDecimal={false}
              value={summary.totalWeightKg}
              disabled
              onChange={noop}
              data-testid="inspection-approved-weight"
            />
          ) : (
            <WeightInput
              label={t("inspections:weighing.approvedWeight")}
              withAsterisk
              value={original.weight}
              onChange={(weight) => form.updateStream(0, { weight })}
              error={error("streams.0.weight")}
              data-testid="inspection-approved-weight"
            />
          )}
          {values.multiFraction && (
            <Text size="xs" color="tertiary">
              {t("inspections:weighing.sumOfFractions")}
            </Text>
          )}
        </div>
        <TextInput
          label={t("inspections:weighing.registrationNumber")}
          placeholder={t("inspections:weighing.registrationNumberPlaceholder")}
          description={t("inspections:weighing.maxCharacters", { max: MAX_REGISTRATION_NUMBER_LENGTH })}
          // Under the field, like the weight's hint, so the two inputs stay level.
          inputWrapperOrder={["label", "input", "description", "error"]}
          value={values.transportRegistrationNumber}
          maxLength={MAX_REGISTRATION_NUMBER_LENGTH}
          onChange={(event) => form.setField("transportRegistrationNumber", event.currentTarget.value)}
        />
      </div>

      {!values.multiFraction && (
        <div className={styles.fieldStack}>
          <FieldLabel required>{t("inspections:photos.label")}</FieldLabel>
          <PhotoInput
            value={original.photos}
            onChange={(photos) => form.updateStream(0, { photos })}
            max={MAX_PHOTOS_PER_STREAM}
            error={error("streams.0.photos")}
            data-testid="inspection-photos"
          />
        </div>
      )}

      {/* Extra fractions can only go on the buyer's other active contracts with the same seller. */}
      {context.eligibleContracts.length > 0 && (
        <div className={styles.fieldStack}>
          <Checkbox
            label={t("inspections:weighing.multipleFractions")}
            checked={values.multiFraction}
            onChange={(event) => form.setMultiFraction(event.currentTarget.checked)}
            data-testid="inspection-multiple-fractions"
          />
          {values.multiFraction && <FractionsTable form={form} summary={summary} context={context} errors={errors} />}
        </div>
      )}

      <div className={styles.twoColumns}>
        <TextInput
          label={t("inspections:weighing.poNumber")}
          placeholder={t("inspections:weighing.poNumberPlaceholder")}
          description={t("inspections:weighing.maxCharacters", { max: MAX_PO_NUMBER_LENGTH })}
          inputWrapperOrder={["label", "input", "description", "error"]}
          value={values.poNumber}
          maxLength={MAX_PO_NUMBER_LENGTH}
          onChange={(event) => form.setField("poNumber", event.currentTarget.value)}
        />
        <TextInput
          label={t("inspections:weighing.comment")}
          value={values.comment}
          onChange={(event) => form.setField("comment", event.currentTarget.value)}
        />
      </div>

      {/* Deviations need the company's deviation products or the contract's services, and a contract. */}
      <div className={`${styles.toggleRow} ${values.hasDeviation ? styles.toggleRowActive : ""}`}>
        <Checkbox
          label={t("inspections:weighing.deviationOnReceipt")}
          checked={values.hasDeviation}
          disabled={!summary.canRegisterDeviations && !values.hasDeviation}
          onChange={(event) => form.setHasDeviation(event.currentTarget.checked)}
          data-testid="inspection-has-deviation"
        />
        <Text size="xs" color="tertiary" className={styles.toggleHint}>
          {summary.canRegisterDeviations
            ? t("inspections:weighing.deviationNextStep")
            : summary.isMarketOrder
              ? t("inspections:weighing.deviationMarketOrder")
              : t("inspections:weighing.deviationUnavailable")}
        </Text>
      </div>
    </div>
  );
}
